<template>
  <div v-if="isVisible">
    <div class="hidden lg:block relative">
      <div class="absolute left-0 right-0 top-1/2 transform -translate-y-1/2 h-0.25 bg-royal-blue-100 z-0">
      </div>
      <div
        class="absolute top-1/2 transform -translate-y-1/2 h-1 bg-royal-blue-100 z-0"
        :style="{ width: `${progressLineWidth}px` }">
      </div>
      <div
        ref="stepsContainer"
        class="flex justify-between pt-4 relative">
        <ProgressStep
          v-for="(step, i) in steps"
          :key="step.id"
          :step="step"
          :ref="el => (stepRefs[i] = el)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue'
import ProgressStep from './ProgressStep.vue'
import { Step } from '@/types'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import type { ComponentPublicInstance } from 'vue'

const route = useRoute()
const store = useStore()

const stepRefs = ref<(Element | ComponentPublicInstance | null)[]>([])
const stepsContainer = ref<HTMLElement | null>(null)
const progressLineWidth = ref(0)

const getIsValid = (key: string) => {
  return store.state.errors[key]?.valid ?? false
}

const steps = computed((): Step[] => {
  const currentStep = route.meta?.step || 1

  return [
    {
      name: 'Manuscript files',
      route: 'upload-files',
      isActive: currentStep === 1,
      isComplete: currentStep > 1 || store.state.errors.uploadFilesVerifyResult.valid,
      isValid: getIsValid('uploadFilesVerifyResult'),
      id: 'uploadFiles',
    },
    {
      name: 'Title & abstract',
      route: 'article-info',
      isActive: currentStep === 2,
      isComplete: currentStep > 2 || store.state.errors.articleInfoVerifyResult.valid,
      isValid: getIsValid('articleInfoVerifyResult'),
      id: 'articleInfo',
    },
    {
      name: 'Authors',
      route: 'author-info',
      isActive: currentStep === 3,
      isComplete: currentStep > 3 || store.state.errors.authorInfoVerifyResult.valid,
      isValid: getIsValid('authorInfoVerifyResult'),
      id: 'authorInfo',
    },
    {
      name: 'Reviewers',
      route: 'review-editorial',
      isActive: currentStep === 4,
      isComplete: currentStep > 4 || store.state.errors.reviewEditorialVerifyResult.valid,
      isValid: getIsValid('reviewEditorialVerifyResult'),
      id: 'reviewEditorial',
    },
    {
      name: 'Final check',
      route: 'final-review',
      isActive: currentStep === 5,
      isComplete: currentStep > 5 || store.state.errors.finalReviewVerifyResult.valid,
      isValid: getIsValid('finalReviewVerifyResult'),
      id: 'finalReview',
    },
  ]
})

function getEl(ref: Element | ComponentPublicInstance | null): HTMLElement | null {
  if (!ref) return null
  return ref instanceof HTMLElement ? ref : (ref as ComponentPublicInstance).$el
}

function updateLineWidth() {
  nextTick(() => {
    const currentStep = route.meta?.step || 1

    const first = getEl(stepRefs.value[0])
    const current = getEl(stepRefs.value[currentStep - 1])

    if (!first || !current) return

    const firstRect = first.getBoundingClientRect()
    const currentRect = current.getBoundingClientRect()

    const firstCenter = firstRect.left + firstRect.width / 2
    const currentCenter = currentRect.left + currentRect.width / 2

    const startOffset = firstRect.width / 2
    progressLineWidth.value = Math.max(0, (currentCenter - firstCenter) + startOffset)
  })
}

const RESIZE_DEBOUNCE_DELAY = 120
let resizeTimer: ReturnType<typeof setTimeout>
const onResize = () => {
  clearTimeout(resizeTimer)
  resizeTimer = setTimeout(updateLineWidth, RESIZE_DEBOUNCE_DELAY)
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  updateLineWidth()
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  clearTimeout(resizeTimer)
})

watch(() => route.meta?.step, () => updateLineWidth())

function resetStepRefsAndUpdateLine() {
  stepRefs.value = []
  updateLineWidth()
}

watch(steps, resetStepRefsAndUpdateLine)

const isVisible = computed(() => !!route.meta?.step)
</script>
